import React from 'react';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { notification } from 'antd';
import { formatNumber } from 'shared/utils';
import { useUpdateRotationOverview } from 'shared/hooks/rotations.hooks';
import { Button } from 'src/components/design-system';
import { TextAreaInput } from 'src/components/design-system/forms';
import { NotFound } from 'src/components/common';
import { useRotationsContext } from '../../../rotations-context';
import { SectionHeading } from '../section-heading';
import styles from './tab-overview.module.scss';

const OVERVIEW_CHAR_LIMIT = 600;

export function OverviewTab() {
  const { lang, selectedRotation: rotation, canEdit } = useRotationsContext();
  const updateRotationOverview = useUpdateRotationOverview();
  const [editMode, setEditMode] = React.useState(false);
  const [api, contextHolder] = notification.useNotification({ placement: 'bottomRight' });

  React.useEffect(() => {
    setEditMode(false);
  }, [rotation?.id]);

  const onSubmit = async ({ overview }) => {
    try {
      await updateRotationOverview.mutateAsync({
        rotationId: rotation.id,
        payload: { overview },
      });
      api.success({ message: lang.OVERVIEW_UPDATE_SUCCESS, duration: 5 });
      setEditMode(false);
    } catch (e) {
      api.error({ message: e.response?.data?.message || lang.OVERVIEW_UPDATE_FAILED, duration: 5 });
    }
  };

  const { handleSubmit, handleChange, values, dirty, resetForm } = useFormik({
    enableReinitialize: true,
    initialValues: { overview: rotation.overview || '' },
    onSubmit,
  });

  const charCount = values.overview?.length || 0;
  const remaining = OVERVIEW_CHAR_LIMIT - charCount;
  const isOver = remaining < 0;
  const counterText = isOver
    ? lang.OVERVIEW_CHARS_OVER.replace('{count}', formatNumber(Math.abs(remaining)))
    : lang.OVERVIEW_CHARS_LEFT.replace('{count}', formatNumber(remaining));

  const handleCancel = () => {
    resetForm();
    setEditMode(false);
  };

  if (!editMode) {
    return (
      <div className={styles.content}>
        {contextHolder}
        <SectionHeading
          title={lang.SUMMARY_AND_OVERVIEW}
          onEdit={() => setEditMode(true)}
          editLabel={lang.EDIT}
          showEdit={canEdit}
        />
        {rotation.overview ? (
          <div className={styles.body}>{rotation.overview}</div>
        ) : (
          <NotFound text={lang.OVERVIEW_EMPTY} />
        )}
      </div>
    );
  }

  return (
    <div className={styles.content}>
      {contextHolder}
      <SectionHeading title={lang.SUMMARY_AND_OVERVIEW} />
      <form onSubmit={handleSubmit}>
        <TextAreaInput
          name="overview"
          value={values.overview}
          onChange={handleChange}
          rows={8}
          placeholder={lang.OVERVIEW_PLACEHOLDER}
        />
        <div className={classNames(styles.counter, isOver && styles.over)}>{counterText}</div>

        <div className={styles.buttons}>
          <Button
            type="primary"
            htmlType="submit"
            loading={updateRotationOverview.isPending}
            disabled={!dirty || isOver}>
            {lang.APPLY_CHANGES}
          </Button>
          <Button
            onClick={handleCancel}
            htmlType="button"
            disabled={updateRotationOverview.isPending}>
            {lang.CANCEL}
          </Button>
        </div>
      </form>
    </div>
  );
}
